
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { sharedState } from '../../../../../framework/state';
import { getResource, putResource } from '../../../../../framework/client/resource';
import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import AutoCompleteField from '../../../../../framework/fields/AutoCompleteField.vue';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { VendorContract } from '../../../../../../common/application/model/VendorContract';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import {
    getAccountOptions,
    getCustomerOptions,
    getVehicleContractOptions,
    getVendorOptions,
} from '../../../../service/application_options_service';
import { VendorContractScope } from '../../../../../../common/application/model/enums/VendorContractScope';
import { PaymentType } from '../../../../../../common/application/model/enums/PaymentType';
import { BudgetType } from '../../../../../../common/application/model/enums/BudgetType';
import { AccountType } from '../../../../../../common/application/model/enums/AccountType';
import DateField from '../../../../../framework/fields/DateField.vue';
import NumberField from '../../../../../framework/fields/NumberField.vue';
import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import { ValidationObserver } from 'vee-validate';
import { Vendor } from '../../../../../../common/application/model/Vendor';

import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { errorToast, successToast } from '../../../../service/toast_service';
import AddDocument from '../../document/AddDocument.vue';

@Component({
    components: {
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        AutoCompleteField,
        ValidationObserver,
        AddDocument,
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            (vm as EditVendorContract).getPrevRouter(from); //get the previous URL
        });
    },
})
export default class EditVendorContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR_CONTRACT;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as VendorContract,
        customerOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        vehicleContractOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractScopeOptions: [] as { id: string | undefined; label: string }[],
        paymentTypeOptions: [] as { id: string | undefined; label: string }[],
        budgetTypeOptions: [] as { id: string | undefined; label: string }[],
        customerExpenseAccountOptions: [] as { id: string | undefined; label: string }[],
        loading: true,
        activeTab: 0,
        prevRoute: '',
        customer: '',
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        this.local.activeTab = this.$route.params.activeTab
            ? Number(this.$route.params.activeTab)
            : Number(localStorage.getItem(`${this.resourceType}_ActiveTab`));
        this.local.row = (await getResource<VendorContract>(this.resourceType, this.id))!!;
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorOptions = await getVendorOptions();
        this.local.vehicleContractOptions = await getVehicleContractOptions([this.local.row.customerId]);
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        this.local.vendorContractScopeOptions = getEnumerationOptions(
            'VendorContractScope',
            Object.keys(VendorContractScope),
            this,
        );
        this.local.paymentTypeOptions = getEnumerationOptions('PaymentType', Object.keys(PaymentType), this);
        this.local.budgetTypeOptions = getEnumerationOptions('BudgetType', Object.keys(BudgetType), this);
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
        localStorage.removeItem(`${this.resourceType}_ActiveTab`);

        this.local.customer = getOptionLabel(this.local.row.customerId, this.local.customerOptions);

        this.local.loading = false;
    }

    @Watch('local.row.customerId')
    async customerIdChange() {
        this.local.vehicleContractOptions = await getVehicleContractOptions([this.local.row.customerId]);
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('local.row.vendorId')
    async vendorIdChange(vendorId: string | undefined) {
        if (vendorId) {
            const vendor = await getResource<Vendor>(ApplicationResource.VENDOR, vendorId);
            if (vendor) {
                this.local.row = {
                    ...this.local.row,
                    vendorNumber: vendor.vendorNumber,
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vendorNumber: '',
            };
        }
    }

    @Watch('local.row.vehicleContractId')
    async vehicleContractIdChange(vehicleContractId: string | undefined) {
        if (vehicleContractId) {
            const vehicleContract = await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                vehicleContractId,
            );
            if (vehicleContract) {
                this.local.row = {
                    ...this.local.row,
                    vehicleRegistrationNumber: vehicleContract.vehicleRegistrationNumber ?? '',
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vehicleRegistrationNumber: '',
            };
        }
    }

    save() {
        this.saveResource();
    }

    saveAndClose() {
        this.saveResource(true);
    }

    private async saveResource(goBack?: boolean) {
        try {
            await putResource<VendorContract>(this.resourceType, this.id, this.local.row);
            successToast(this, 'message.vendorContractSaved');

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get vendorLocationSelectorParams(): Map<string, string> {
        const params = new Map();

        if (this.local.row.vendorId) {
            params.set('parentId', this.local.row.vendorId);
        }

        return params;
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 1;
        }
    }
}
